import React, { useState, useEffect, useRef } from 'react';
import { Flame } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import styles from './StreakIcon.module.css';
import { authenticatedFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const StreakInfo = ({ onClose }) => {
    const [streakData, setStreakData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setCurrentStreak } = useAuth();
    const location = useLocation();

    useEffect(() => {
        fetchStreakInfo();
        // eslint-disable-next-line
    }, []);

    const fetchStreakInfo = async () => {
        try {
            setLoading(true);
            const data = await authenticatedFetch('/api/streak/info');
            setStreakData(data);
            setCurrentStreak(data.current_streak || 0);
        } catch (err) {
            setError('Unable to load streak information');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        try {
            setLoading(true);
            await authenticatedFetch('/api/streak/reset', {
                method: 'POST'
            });
            await fetchStreakInfo();
        } catch (err) {
            setError('Unable to reset streak');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className={styles.loading}>Loading streak data...</div>;
    if (error) return <div className={styles.error}>{error}</div>;
    if (!streakData) return null;

    const lastActivityDate = new Date(streakData.last_activity);
    const nextResetAvailable = streakData.last_reset_date
        ? new Date(streakData.last_reset_date).setDate(new Date(streakData.last_reset_date).getDate() + 30)
        : null;

    const daysSinceLastActivity = Math.floor((new Date() - lastActivityDate) / (1000 * 60 * 60 * 24));
    const isStreakBroken = daysSinceLastActivity > 1;

    const isResetAvailable = nextResetAvailable && new Date() > new Date(nextResetAvailable);

    // console.log("isStreakBroken:", isStreakBroken, "streak_resets_remaining", streakData.streak_resets_remaining, "nextResetAvailable", nextResetAvailable, "isResetAvailable", isResetAvailable);

    return (
        <div className={styles.streakInfo}>
            <div className={styles.header}>
                <h3>Practice Streak</h3>
                <div className={styles.indicator}>
                    <Flame size={16} />
                    <span>{streakData.current_streak} Days</span>
                </div>
            </div>

            <div className={styles.metrics}>
                <div className={styles.metricCard}>
                    <span className={styles.metricLabel}>Current Streak</span>
                    <span className={styles.metricValue}>{streakData.current_streak}</span>
                    <span className={styles.metricUnit}>days</span>
                </div>
                <div className={styles.metricCard}>
                    <span className={styles.metricLabel}>Longest Streak</span>
                    <span className={styles.metricValue}>{streakData.longest_streak}</span>
                    <span className={styles.metricUnit}>days</span>
                </div>
            </div>

            <div className={styles.activity}>
                <span className={styles.activityLabel}>Last Practice</span>
                <span className={styles.activityDate}>
                    {lastActivityDate.toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </span>
            </div>

            <div className={styles.actionSection}>
                {(location.pathname !== '/practice') && (
                    <Link to="/practice" className={styles.practiceButton}>
                        Move to Practice Tab
                    </Link>
                )}

                {isStreakBroken && streakData.streak_resets_remaining > 0 && isResetAvailable && (
                    <button
                        className={styles.resetButton}
                        onClick={handleReset}
                    >
                        Reset Broken Streak
                    </button>
                )}
                {nextResetAvailable && !isResetAvailable && (
                    <span className={styles.resetAvailable}>
                        Next reset available: {new Date(nextResetAvailable).toLocaleDateString()}
                    </span>
                )}
            </div>
        </div>
    );
};

const StreakIcon = () => {
    const [showInfo, setShowInfo] = useState(false);
    const ref = useRef(null);
    const { currentStreak } = useAuth();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setShowInfo(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={styles.streakContainer} ref={ref}>
            <button
                className={styles.streakButton}
                onClick={() => setShowInfo(!showInfo)}
                aria-label="View streak information"
            >
                <Flame size={20} />
                <p className={styles.streakCount}>{currentStreak}</p>
            </button>
            {showInfo && <StreakInfo onClose={() => setShowInfo(false)} />}
        </div>
    );
};

export default StreakIcon;